/**
 * What a failed submit means to the person filing it.
 *
 * The transport (casesApi.ts) decides *whether* a call failed and with which stable
 * code; this module decides what the user is told and how the feature recovers. The
 * message text is the portal's own — chosen from the code, never taken from whatever
 * prose arrived over the wire — so it is reviewable here rather than assembled at the
 * boundary.
 *
 * A failure is a plain, serializable object rather than an `Error` subclass on purpose:
 * it rides RTK Query's `queryFn` result and is what `.unwrap()` rejects with, so it must
 * survive the store without tripping the serializability check.
 */
import type { ApiErrorCode } from './contracts';

/**
 * What the user is told, per code.
 *
 * Anything unrecognized falls back rather than being displayed.
 */
const MESSAGE_FOR_CODE: Record<ApiErrorCode, string> = {
  InvalidRequest: 'Some of the details on this request could not be accepted. Please review and try again.',
  Unauthenticated: 'Your session has expired. Sign in again to continue.',
  NotFound: 'That case could not be found.',
  Conflict: 'This submission has already been sent. Please start a new request rather than resending this one.',
  DownstreamUnavailable: 'The request could not be filed right now. Please try again shortly.',
  DownstreamRejection: 'The request could not be filed. Please contact support if this continues.',
  DownstreamThrottled: 'The case service is busy right now. Please wait a moment and submit again.',
  Timeout: 'The request took too long to confirm. It may already have been filed — please check before resubmitting.',
  DeserializationError: 'The request could not be filed. Please contact support if this continues.',
  InvalidResponse: 'The request could not be filed. Please contact support if this continues.',
  CaseFilingIncomplete:
    'The case was filed, but not every account on it was recorded. Do not resubmit.',
  UnhandledException: 'Something went wrong while filing the request. Please try again shortly.',
  Unknown: 'Something went wrong while filing the request. Please try again shortly.',
};

export const FALLBACK_MESSAGE = 'Something went wrong while filing the request. Please try again shortly.';

/** A transport-level failure that never reached a coded API answer. */
export type FailureCode = ApiErrorCode | 'TransportFailure';

export function messageForCode(code: string | undefined): string {
  if (code !== undefined && code in MESSAGE_FOR_CODE) {
    return MESSAGE_FOR_CODE[code as ApiErrorCode];
  }

  return FALLBACK_MESSAGE;
}

/** A submit or upload that did not produce the result the caller asked for. */
export interface ApiFailure {
  code: FailureCode;
  /** The HTTP status the failure rode in on, or 0 when the call never completed. */
  status: number;
  /** The portal-owned message, already chosen from the code. */
  message: string;
  /**
   * The identifier of the case the failure is about, when there is one.
   *
   * Set for CaseFilingIncomplete and for every attachment failure, where a case exists
   * and must not be filed again.
   */
  caseId?: string;
}

/** Builds the failure for a case submit that did not produce a case. */
export function submitFailure(code: FailureCode, status: number, caseId?: string): ApiFailure {
  let message = code === 'TransportFailure' ? FALLBACK_MESSAGE : messageForCode(code);

  // CaseFilingIncomplete means a real case exists but is missing part of what was asked
  // for, so the user is told to quote it to support. That instruction is only actionable
  // if the number is in front of them: the retained caseId is folded into the message
  // rather than left in a field nothing renders.
  if (code === 'CaseFilingIncomplete' && caseId !== undefined) {
    message = `${message} Quote case ${caseId} to support.`;
  }

  return {
    code,
    status,
    message,
    caseId,
  };
}

/**
 * Builds the failure for a case that was filed but whose attachments could not be sent.
 *
 * Carries the case id because the case is real and already filed — the caller must be
 * able to say which one is now missing its instrument rather than treat the whole submit
 * as failed and invite a duplicate.
 */
export function attachmentFailure(code: FailureCode, caseId: string): ApiFailure {
  const base = code === 'TransportFailure' ? FALLBACK_MESSAGE : messageForCode(code);
  return {
    code,
    status: 0,
    message: `${base} The case was filed as ${caseId}; do not submit it again.`,
    caseId,
  };
}

/** Whether an unknown thrown value is one of our failures. */
export function isApiFailure(value: unknown): value is ApiFailure {
  return (
    typeof value === 'object' &&
    value !== null &&
    'code' in value &&
    'message' in value &&
    typeof (value as ApiFailure).message === 'string'
  );
}
